<script setup>
import {reactive, onMounted, computed, watch, nextTick, ref} from "vue";
import mapboxgl from "mapbox-gl";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import { DatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";

const compName = 'AppointmentsMapComponent';

const props = defineProps({
  contextSchemaA: String,
  contextIdA: String
});

const form = ref(null);

const data = reactive({
  appointments: [],
  date: undefined,
  dateFormatted: '',
  defaultItem: {
    date: '',
    time: '',
    streetaddress: '',
    city: '',
    state: '',
    zip: '',
    latitude: null,
    longitude: null,
    persontomeet: '',
    reason: '',
    status: '',
  },
  editedItem: {
    date: '',
    time: '',
    streetaddress: '',
    city: '',
    state: '',
    zip: '',
    latitude: null,
    longitude: null,
    persontomeet: '',
    reason: '',
    status: '',
  },
  isEditing: false,
  isPending: false,
  loading: true,
  map: null,
  mapMarkers: [],
  menu1: false,
  menu: false,
  openAppointmentForm: false,
  searchRequest: null,
  states: [ 'AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FM', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'MP', 'OH', 'OK', 'OR', 'PW', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VI', 'VA', 'WA', 'WV', 'WI', 'WY' ],
  time: null,
  timezone: new Date(),
  valid: true,
  xactId: null,
  symbilityId: null
});

const csvData = computed(()=>{
  // CSV File
  return data.appointments.map((item) => ({
    ...item,
    time: item.time,
    date: item.date,
    streetaddress: item.streetaddress,
    city: item.city,
    state: item.state,
    zip: item.zip,
    status: item.status,
  }))
})

const formatDateTime = (date) => {
  if (!date) {
    return null
  }
  else{
    let thisDate = new Date(date);
    return thisDate.toLocaleDateString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
  }
}

/****************************************************/
const cancel = () => {
  data.editedItem = Object.assign({}, data.defaultItem)
  data.openAppointmentForm = false
}

const close = () => {
  data.openAppointmentForm = false
  nextTick(() => {
    data.editedItem = Object.assign({}, data.defaultItem)
    data.isEditing = false
  })
}

const complete = (item) => {
  data.loading = true
  data.editedItem = JSON.parse(JSON.stringify(item))
  data.editedItem.status = 'Complete'
  ApiWrapper.post('appointments', 'edit-appointment-map', {
        editedItem: data.editedItem
      })
      .then((response) => {
        listAll()
        close();

        ToastUtil.successMsg('Updated appointment');
      })
      .catch((e) => {
        ToastUtil.error(e);

        data.loading = false;
        close();
      });
}

const createAppointment = (long, lat) => {
  data.editedItem.longitude = long
  data.editedItem.latitude = lat
  ApiWrapper.post('appointments', 'create-appointment', {
        editedItem: data.editedItem,
        claimId: props.contextIdA,
      })
      .then((response) => {
        listAll()
        close()

        ToastUtil.successMsg('Created appointment');
      })
      .catch((err) => {
        ToastUtil.error(err);

        data.loading = false;
        close();
      })
}

const csvExport = (arrData) => {
  // CSV File
  let csvContent = 'data:text/csv;charset=utf-8,'
  csvContent += [
    Object.keys(arrData[0]).join(';'),
    ...arrData.map((item) => Object.values(item).join(';')),
  ]
      .join('\n')
      .replace(/(^\[)|(\]$)/gm, '')

  const data = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', data)
  link.setAttribute('download', 'export.csv')
  link.click()
}

const deleteItem = (item) => {
  let deleteMessage = confirm(
      'Are you sure you want to delete this item?'
  )
  if (deleteMessage === true) {
    data.loading = true
    ApiWrapper.post('appointments', 'delete-appointment', {
          item: {id: item.id},
          claimId: props.contextIdA,
        })
        .then((response) => {
          listAll()
          ToastUtil.successMsg('Deleted appointment');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        })
  }
}

const editItem = (item) => {
  data.isEditing = true
  data.openAppointmentForm = true
  data.editedItem = JSON.parse(JSON.stringify(item))
  data.date = data.editedItem.date
  data.dateFormatted = formatDateTime(data.editedItem.date+"T"+data.editedItem.time+":00");
}


const listAll = () => {
  data.loading = true;
  ApiWrapper.post('appointments', 'list-appointments-claim', {
        claimId: props.contextIdA,
      })
      .then((response) => {
        data.appointments = response.data;
        for (let x in data.appointments) {
          if (data.appointments[x].status === 'Pending') {
            data.isPending = true
          }
        }
        for(let x in response.data){
          response.data[x].formattedDateTime = formatDateTime(response.data[x].date+"T"+response.data[x].time+":00");
        }
        if(response.xactId){
          data.xactId = response.xactId;
        }
        if(response.symbilityId){
          data.symbilityId = response.symbilityId;
        }
      })
      .catch((err) => {
        ToastUtil.error(err);
      })
      .finally(()=> {
        close()
        data.loading = false
      })
}

const pend = (item) => {
  data.loading = true
  data.editedItem = JSON.parse(JSON.stringify(item))
  data.editedItem.status = 'Pending'
  ApiWrapper.post('appointments', 'edit-appointment-map', {
        editedItem: data.editedItem
      })
      .then((response) => {
        listAll()
        close()

        ToastUtil.successMsg('Updated appointment');
      })
      .catch((err) => {
        ToastUtil.error(err);

        data.loading = false;
        close();
      })
}

const submit = () => {
  
  if (form.value.validate()) {
    data.openAppointmentForm = false
    data.loading = true
    if (data.isEditing) {
      let encodedSearch = encodeURI(

          data.editedItem.streetaddress.replace('#', '') +
          `, ` +
          data.editedItem.city +
          `, ` +
          data.editedItem.state +
          ` ` +
          data.editedItem.zip
      )
      ApiWrapper.getRaw(
            'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
            encodedSearch +
            '.json?access_token=pk.eyJ1IjoiZW50cmVvcyIsImEiOiJja2RydG16MXEwZ3hvMnhuOXVlcHZhcWRvIn0.mlZA3MAQSFupaQbmFjA9QQ'
      )
      .then(function (response) {
        data.editedItem.longitude =
            response.features[0].center[0].toString()
        data.editedItem.latitude =
            response.features[0].center[1].toString()
        // data.editedItem.time = dayjs(`${data.editedItem.date} ${data.editedItem.time}`).toISOString().split('T')[1]
        const [date, time] = new Date(data.date).toISOString().split('T');
        data.editedItem.date = date;
        data.editedItem.time = time;
        ApiWrapper.post('appointments', 'edit-appointment', {
              editedItem: data.editedItem,
            })
            .then((response) => {
              close()
              activeComponentStore.notifyComponents(['appointments'],compName);
              listAll()
              ToastUtil.successMsg('Updated appointment');
            })
            .catch((err) => {
              ToastUtil.error(err);
              data.loading = false;
              close();
            })
      })
      .catch((err) => {
        ToastUtil.error(err);

        data.loading = false;
        close();
      })
    } else {
      let encodedSearch = encodeURI(
          data.editedItem.streetaddress.replace('#', '') +
          `, ` +
          data.editedItem.city +
          `, ` +
          data.editedItem.state +
          ` ` +
          data.editedItem.zip
      )
      data.editedItem.status = 'Pending'
      ApiWrapper.getRaw(
            'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
            encodedSearch +
            '.json?access_token=pk.eyJ1IjoiZW50cmVvcyIsImEiOiJja2RydG16MXEwZ3hvMnhuOXVlcHZhcWRvIn0.mlZA3MAQSFupaQbmFjA9QQ'
      )
      .then(function (response) {
        let longitude = response.features[0].center[0].toString()
        let latitude = response.features[0].center[1].toString()
        createAppointment(longitude, latitude)
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
        close();
      })
    }
  }
}

onMounted(()=>{
  mapboxgl.accessToken =
      'pk.eyJ1IjoiZW50cmVvcyIsImEiOiJja2RydG16MXEwZ3hvMnhuOXVlcHZhcWRvIn0.mlZA3MAQSFupaQbmFjA9QQ'

  listAll()

});


</script>

<template>
  <v-container fluid >
    <v-card elevation="2" v-if="data.loading === false">
      <v-toolbar density="compact" flat color="white">
        <v-toolbar-title>Appointments</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-icon @click="csvExport(csvData)" v-if="data.appointments" class="text-right"> mdi-file-download </v-icon>

        <v-spacer></v-spacer>
      </v-toolbar>
      <!-- New Appointment Button -->
      <div v-if="data.openAppointmentForm">
        <v-card flat>
          <v-list>
            <v-list-item density="compact">
                <v-list-item-title v-if="!data.isEditing" class="primary--text"
                >New Appointment</v-list-item-title
                >
                <v-list-item-title
                    v-else
                    class="primary--text"
                >Edit Appointment</v-list-item-title
                >
            </v-list-item>
          </v-list>
          <v-form ref="form" v-model="data.valid" lazy-validation>
            <v-list>
              <v-list-item density="compact">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-menu
                        ref="menu"
                        v-model="data.menu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="auto"
                    >
                      <template v-slot:activator="{ on, props }">
                        <v-text-field
                            v-model="data.dateFormatted"
                            label="Date"
                            hint="MM/DD/YYYY format"
                            persistent-hint
                            prepend-icon="mdi-calendar"
                            v-bind="props"
                            v-on="on"
                        ></v-text-field>
                      </template>
                      <DatePicker
                          v-model="data.dateFormatted"
                          mode="dateTime"
                          :rules="{
                              minutes:
                              [0, 15, 30, 45],
                            }"
                          @update:modelValue="data.date=$event; data.dateFormatted = formatDateTime(data.date); data.editedItem.formattedDateTime = formatDateTime(data.date); data.editedItem.date = data.date;"
                      >

                      </DatePicker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.reason"
                        label="Reason"
                        :rules="[v => !!v || 'A reason is required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.persontomeet"
                        label="Person to meet"
                        :rules="[v => !!v || 'A person to meet is required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.streetaddress"
                        label="Address"
                        :rules="[v => !!v || 'Address is required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.city"
                        label="City"
                        :rules="[v => !!v || 'City is required']"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-autocomplete
                        v-model="data.editedItem.state"
                        :items="data.states"
                        label="State"
                        auto-select-first
                        :rules="[v => !!v || 'State is required']"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.zip"
                        label="Zip Code"
                        :rules="[v => !!v || 'Zip Code is required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" align="left">
                    <v-btn
                        size="small"
                        variant="elevated"
                        color="error"
                        :disabled="data.loading"
                        @click="cancel">Cancel
                    </v-btn>
                    <v-btn
                        size="small"
                        variant="elevated"
                        color="primary"
                        @click="submit"
                        :disabled="!data.valid"
                        :loading="data.loading"
                    >Submit
                    </v-btn>
                  </v-col>
                </v-row>
              </v-list-item>
            </v-list>
          </v-form>
        </v-card>
      </div>

      <v-list two-line class="py-0">
        <v-divider></v-divider>
        <v-alert v-if="data.appointments && (data.xactId !== null || data.symbilityId !== null)" type="info" text="Appointment updates and deletions will not be communicated to third parties by Claimslink."></v-alert>
        <template v-for="item in data.appointments" :key="item.id">
          <v-list-item density="compact">
              <v-list-item-title v-if="item.formattedDateTime !== undefined">
                <strong>
<!--                  {{ item.formattedDateTime }} - {{ getTimezone(data.timezone) }}-->
                  {{item.formattedDateTime}}
                </strong>
              </v-list-item-title>
              <v-list-item-title>
                {{item.reason + ` with ` + item.persontomeet}}
              </v-list-item-title>
              <v-list-item-subtitle v-if="item.streetaddress != null || item.city != null || item.state != null || item.zip != null" class="mb-2 pb-2">
                {{item.streetaddress + ', ' + item.city + ', ' + item.state + ' ' + item.zip}}
              </v-list-item-subtitle>
              <v-list-item-subtitle v-else>
                Incomplete/no address. Please edit.
              </v-list-item-subtitle>

            <v-list-item-action v-if="item.status === 'Pending'">
              <v-icon v-if="Permissions.checkPermissions(['appointments.update'])" size="small" class="m-2" @click="editItem(item)">
                mdi-pencil
              </v-icon>
              <v-icon v-if="Permissions.checkPermissions(['appointments.delete'])" size="small" class="m-2" @click="deleteItem(item)">
                mdi-delete
              </v-icon>
            </v-list-item-action>

            <v-list-item-action>
              <v-list-item-subtitle
                  v-if="item.status === 'Pending'"
                  class="error--text mb-2 pb-2"
              >Pending
              </v-list-item-subtitle>
              <v-list-item-subtitle
                  v-if="item.status === 'Complete'"
                  class="success--text mb-2 pb-2"
              >Complete
              </v-list-item-subtitle>
            </v-list-item-action>
          </v-list-item>

          <v-divider></v-divider>
        </template>
      </v-list>
    </v-card>
  </v-container>
</template>

<style scoped>
  .container {
    padding: 5px;
  }
  
  .mapboxgl-map {
    margin: 0;
    width: 90%;
    height: 300px;
  }
  
  .mapboxgl-popup-content {
    width: 520px;
  }
</style>